'use client'

import { useState } from 'react'
import { CartItemFragment } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { GetProductAccessories } from '@cartsite-app/src/graphql/get-product-accessories.graphql'
import { GetProductAccessoriesQuery } from '@cartsite-app/src/graphql/generated/graphql-types'
import { ProductCardGallery, ProductTypeProps } from '@dustin-web/product-components/src/cards'
import { useTranslations } from '@dustin-web/microsite-components/src/hooks'
import ExpandButton from '@dustin-web/microsite-components/src/expand-button'
import { getItemAccessoryId } from './cart-utils'
import CartItemAccessoryCard from './cart-item-accessory-card'
import { makeVar } from '@dustin-web/microsite-components/src/hooks/use-reactive-var'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'
import { ProductDigitalData } from '@dustin-web/product-components/src/utils/product-digital-data'
import OnScreen from '@dustin-web/microsite-components/src/on-screen'
import { getProductLabels } from '@dustin-web/product-components/src/cards/utils'
import { useShoppingCart } from '@dustin-web/product-components/src/hooks/dataloader/use-shopping-cart'

export const productErpIdentifiersWithAccessoriesVar = makeVar(new Set<string>())

export const CartAccessories = () => {
  const { cartItems } = useShoppingCart({
    withRequiredEndUserInformation: true,
  })
  const mainItems = cartItems.filter(i => !i.mainProductErpIdentifier)

  return (
    <div className="c-shoppingcart__accessories">
      {mainItems.map(cartItem => (
        <CartItemAccessories cartItem={cartItem} key={cartItem.id} />
      ))}
    </div>
  )
}

type CartItemAccessoriesProps = {
  cartItem: CartItemFragment
}

const CartItemAccessories = ({ cartItem }: CartItemAccessoriesProps) => {
  const { cartItems } = useShoppingCart({
    withRequiredEndUserInformation: true,
  })

  //! Future A/B-test
  const cartAccessoriesGallery = false

  const { data } = useGraphql<GetProductAccessoriesQuery>(GetProductAccessories, {
    variables: {
      productErpIdentifier: cartItem.productErpIdentifier,
    },
    swrOptions: {
      onSuccess: (accessoryData: GetProductAccessoriesQuery) => {
        const existingItemIds = productErpIdentifiersWithAccessoriesVar()
        const hasDependentAccessories = accessoryData?.accessories?.dependentAccessories.some(
          a => !!a.id
        )

        if (hasDependentAccessories) {
          productErpIdentifiersWithAccessoriesVar(
            new Set(existingItemIds.add(cartItem.productErpIdentifier))
          )
        }
      },
    },
  })

  const { translate } = useTranslations()
  const [showMore, setShowMore] = useState(false)
  const accessories = data?.accessories?.dependentAccessories

  if (!accessories || accessories.length === 0) {
    return null
  }
  function pushViewItemList() {
    if (accessories) {
      ProductDigitalData.pushViewItemList(
        accessories?.slice(0, numberOfAccessoriesToShow).map((accessory, i) => ({
          ...accessory,
          productListIndex: i,
          label: getProductLabels(accessory as ProductTypeProps),
        })),
        'shoppingCartAccessories'
      )
    }
  }
  const defaultNumberOfAccessoriesToShow = 3
  const numberOfAccessoriesToShow = showMore
    ? accessories?.length
    : defaultNumberOfAccessoriesToShow

  const cartItemId = getItemAccessoryId(cartItem)

  if (!cartAccessoriesGallery) {
    return (
      <section id={cartItemId} className="u-mb-32">
        <h2 className="h3">{`${translate('AccessoriesForProduct')} ${cartItem.product?.displayName || ''
          }`}</h2>
        <OnScreen handleOnScreen={pushViewItemList} className="c-box u-px-0 u-py-0">
          <ul className="c-shoppingcart ul-stripped">
            {accessories?.slice(0, numberOfAccessoriesToShow).map((accessory, i) => (
              <li
                className="c-shoppingcart__item c-shoppingcart__item--accessory"
                key={accessory.productErpIdentifier}>
                <CartItemAccessoryCard
                  accessory={accessory}
                  mainProductErpIdentifier={cartItem.productErpIdentifier || null}
                  mainProductQuantity={cartItem.quantity}
                  accessoryQuantity={
                    cartItems.find(
                      x =>
                        x.productErpIdentifier === accessory.productErpIdentifier &&
                        x.mainProductErpIdentifier === cartItem.productErpIdentifier &&
                        x.parentCartItemId === cartItem.id
                    )?.quantity || 0
                  }
                  parentCartItemId={cartItem.id}
                  digitalDataIndex={i}
                />
              </li>
            ))}
          </ul>
        </OnScreen>
        {accessories.length > defaultNumberOfAccessoriesToShow && (
          <ExpandButton
            expanded={showMore}
            collapsedTitle={`${translate('ShowMore')} (${accessories.length - defaultNumberOfAccessoriesToShow
              })`}
            expandedTitle={translate('ShowLess')}
            onClick={() => setShowMore(!showMore)}
            ariaControls={cartItemId}
          />
        )}
      </section>
    )
  } else {
    return (
      <section id={cartItemId}>
        <h2 className="h3">{`${translate('AccessoriesForProduct')} ${cartItem.product?.displayName || ''
          }`}</h2>
        <div className="u-mb-32">
          <div className="row sm-gutters">
            {accessories.slice(0, numberOfAccessoriesToShow).map(accessory => (
              <ProductCardGallery
                key={accessory.id}
                product={accessory}
                ticket={''}
                sizeClasses="col-12 col-md-4"
                mainProductErpIdentifier={cartItem.productErpIdentifier}
                digitalDataOrigin="shoppingCartPage"
              />
            ))}
          </div>
          {accessories.length > defaultNumberOfAccessoriesToShow && (
            <ExpandButton
              expanded={showMore}
              collapsedTitle={`${translate('ShowMore')} (${accessories.length - defaultNumberOfAccessoriesToShow
                })`}
              expandedTitle={translate('ShowLess')}
              onClick={() => setShowMore(!showMore)}
              ariaControls={cartItemId}
            />
          )}
        </div>
      </section>
    )
  }
}
